/**
 * LE formulaire de demande de devis — le seul.
 *
 * Il y avait trois copies JSX du même formulaire, toutes branchées sur le même
 * hook `useFormulaireDevis` : /contact-devis, /contact et ContactForm, qui
 * servait l'accueil, les pages communes, /zones-intervention et une satellite.
 * La logique était mise en commun depuis longtemps ; le rendu, lui, avait
 * divergé — deux services ici, quatre là, une tranche de volume absente, un
 * lien vers l'estimateur photo sur deux copies et pas sur la troisième.
 *
 * Ce composant est la source unique. Ce qui varie d'une page à l'autre passe
 * par des props de PRÉSENTATION, jamais par une seconde copie des champs :
 *
 *   variante        `page` (défaut) — carte blanche, labels visibles, textarea
 *                   haute : /contact-devis, /contact, pied de page.
 *                   `carte` — version compacte pour la colonne droite du hero.
 *   titre…          en-tête optionnel, propre au contexte.
 *   villeParDefaut  amorce de « Ville de départ » sur les pages communes.
 *   ancre           id du bloc, pour y faire défiler la page ; il complète
 *                   aussi la page d'origine transmise à la soumission.
 *   annoterPourAgents
 *                   annotations WebMCP (`toolname`, `toolparamdescription`).
 *                   À désactiver sur le SECOND formulaire d'une même page :
 *                   deux formulaires annotés du même `toolname` exposeraient
 *                   deux outils identiques à un agent.
 *
 * Le point d'entrée vers l'estimateur de volume par photos n'existe plus ici.
 * La route /estimation-volume reste servie et accessible par la navigation.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from '../lib/motion';
import {
  ArrowRight,
  Loader as Loader2,
  CircleCheck,
  Shield,
  Truck,
  Warehouse,
  Globe,
  ArrowUpFromLine,
} from 'lucide-react';
import {
  SERVICES, VOLUMES, useFormulaireDevis, DESCRIPTIONS_AGENT, descriptionDuChoix,
  DESCRIPTION_OUTIL_DEVIS, NOM_OUTIL_DEVIS, type ChampsDevis,
} from '../lib/devis';
import { libelleAvecRepere } from '../lib/devis-schema';
import type { SourceDevis } from '../lib/mesure';

const ICONES: Record<string, typeof Truck> = {
  'demenagement': Truck,
  'garde-meuble': Warehouse,
  'international': Globe,
  'monte-meubles': ArrowUpFromLine,
};

// Descriptions des deux champs à choix, composées à partir des options
// réellement rendues. Le formulaire unique rend les listes COMPLÈTES du socle :
// il n'y a plus de sous-ensemble, donc plus de promesse que l'`enum` ne tient
// pas.
const DESCRIPTION_SERVICE = descriptionDuChoix('service', SERVICES);
const DESCRIPTION_VOLUME = descriptionDuChoix('volume', VOLUMES);

/** Clé du brouillon déposé par l'estimateur de volume (EstimationVolumePage). */
const CLE_BROUILLON = 'gramme_devis_draft';

export interface DevisFormProps {
  /** Origine, pour la mesure de conversion et la colonne `source_page`. */
  source: SourceDevis;
  variante?: 'page' | 'carte';
  /** Petite ligne au-dessus du titre, en jaune. */
  surtitre?: string;
  titre?: string;
  sousTitre?: string;
  /** Commune pré-remplie dans « Ville de départ ». Le champ reste modifiable. */
  villeParDefaut?: string;
  /** Valeurs initiales supplémentaires — report d'URL, par exemple. */
  valeursInitiales?: Partial<ChampsDevis>;
  /**
   * Préfixe des `id` HTML. Deux formulaires cohabitent sur l'accueil et les
   * pages communes ; sans préfixe distinct, leurs `<label for>` se croisent.
   */
  prefixeId?: string;
  /** id du bloc (cible d'un lien `#ancre`) et fragment de la page d'origine. */
  ancre?: string;
  /** Annotations WebMCP. `false` sur le second formulaire d'une même page. */
  annoterPourAgents?: boolean;
}

export default function DevisForm({
  source,
  variante = 'page',
  surtitre,
  titre,
  sousTitre,
  villeParDefaut,
  valeursInitiales,
  prefixeId = 'devis',
  ancre,
  annoterPourAgents = true,
}: DevisFormProps) {
  const carte = variante === 'carte';
  const p = prefixeId;

  // Page d'origine du lead : chemin de la page, complété par l'ancre du bloc
  // quand la page en porte deux. `useLocation` est servi par StaticRouter au
  // pré-rendu comme par BrowserRouter dans le navigateur : pas de `window`.
  const { pathname } = useLocation();
  const pageOrigine = ancre ? `${pathname}#${ancre}` : pathname;

  // Annotation conditionnelle. `undefined` fait omettre l'attribut par React.
  const agent = <T,>(valeur: T): T | undefined => (annoterPourAgents ? valeur : undefined);

  const [estimationId, setEstimationId] = useState<string | null>(null);

  const { form, set, remplacer, errors, submitted, loading, success, erreurEnvoi, envoyer } =
    useFormulaireDevis({
      source,
      pageOrigine,
      valeursInitiales: {
        ...(villeParDefaut ? { cityFrom: villeParDefaut } : {}),
        ...valeursInitiales,
      },
      suffixeMessage: estimationId ? `[estimation_id: ${estimationId}]` : undefined,
    });

  // Retour depuis l'estimateur de volume : pré-remplit la tranche et rattache
  // l'estimation au devis via son id, ajouté au message sans être affiché.
  //
  // Cette lecture vivait dans ContactForm seulement. Or l'estimateur renvoyait
  // vers /contact-devis, qui ne la faisait pas : le volume mesuré était perdu
  // à l'arrivée. Elle vit désormais dans le formulaire unique, donc partout.
  useEffect(() => {
    try {
      const brut = sessionStorage.getItem(CLE_BROUILLON);
      if (!brut) return;
      const brouillon = JSON.parse(brut);
      if (brouillon.estimation_id) setEstimationId(brouillon.estimation_id);
      if (typeof brouillon.volume_m3 === 'number') {
        const tranche =
          brouillon.volume_m3 < 20 ? '<20'
          : brouillon.volume_m3 <= 50 ? '20-50'
          : brouillon.volume_m3 <= 100 ? '50-100'
          : '>100';
        remplacer({
          volume: tranche,
          message: `Volume estimé via l'outil photos : ${brouillon.volume_m3} m³.`,
        });
      }
    } catch { /* brouillon corrompu — ignoré */ }
  }, []);

  const classeCarte = carte
    ? 'bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-6 border border-white/60'
    : 'bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm';

  const labelClass = carte
    ? 'block text-[11px] font-bold uppercase tracking-wider text-navy/70 mb-1'
    : 'block text-sm font-medium text-navy mb-1.5';

  const inputBase = carte
    ? 'w-full border-2 rounded-lg px-3 py-2.5 text-sm outline-none transition-all bg-white focus:ring-2 placeholder:text-gray-400'
    : 'w-full border rounded-xl px-4 py-3.5 text-base outline-none transition-all duration-200 bg-white focus:ring-2 placeholder:text-muted/60';

  const inputClass = (key: keyof ChampsDevis) =>
    `${inputBase} ${
      errors[key]
        ? 'border-red-400 focus:ring-red-300'
        : carte
          ? 'border-gray-200 focus:border-yellow focus:ring-yellow/20'
          : 'border-gray-200 focus:ring-navy/20 focus:border-navy'
    }`;

  const erreur = (key: keyof ChampsDevis, id: string) =>
    submitted && errors[key] ? (
      <p id={id} className="text-red-500 text-xs mt-1">{errors[key]}</p>
    ) : null;

  const describedBy = (key: keyof ChampsDevis, id: string) =>
    submitted && errors[key] ? id : undefined;

  return (
    <div id={ancre} className={`${classeCarte}${ancre ? ' scroll-mt-24' : ''}`}>
      {(surtitre || titre || sousTitre) && (
        <div className={carte ? 'mb-5' : 'mb-8'}>
          {surtitre && (
            <p className="text-yellow text-[11px] font-bold uppercase tracking-[0.2em] mb-1">{surtitre}</p>
          )}
          {titre && (
            <h2 className={carte ? 'text-navy font-black text-xl uppercase' : 'text-2xl md:text-3xl font-black uppercase text-navy leading-tight'}>
              {titre}
            </h2>
          )}
          {sousTitre && (
            <p className={carte ? 'text-muted text-sm mt-1' : 'text-muted text-base mt-2'}>{sousTitre}</p>
          )}
        </div>
      )}

      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${carte ? 'mb-4 px-4 py-3' : 'mb-8 px-6 py-5'} bg-navy text-yellow rounded-xl flex items-start gap-3`}
        >
          <CircleCheck className="w-6 h-6 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">Demande envoyée avec succès !</p>
            <p className="text-yellow/80 text-sm mt-1">Nous vous recontacterons sous 24h ouvrables.</p>
          </div>
        </motion.div>
      )}

      <form
        method="POST"
        onSubmit={envoyer}
        noValidate
        toolname={agent(NOM_OUTIL_DEVIS)}
        tooldescription={agent(DESCRIPTION_OUTIL_DEVIS)}
      >
        {/* Le type de prestation est un groupe de boutons radio dans un
            <fieldset> nommé, et non des <button type="button"> : pour un
            agent comme pour un lecteur d'écran, c'est le fieldset qui
            représente le champ. L'input est masqué, le <label> porte le style. */}
        <fieldset className={carte ? 'mb-4' : 'mb-8'} toolparamdescription={agent(DESCRIPTION_SERVICE)}>
          <legend className={carte ? 'sr-only' : 'block text-navy font-bold text-sm uppercase tracking-wider mb-3'}>
            Type de service
          </legend>
          <div className={carte ? 'grid grid-cols-4 gap-2' : 'grid grid-cols-2 sm:grid-cols-4 gap-3'}>
            {SERVICES.map(({ libelle, valeur }) => {
              const Icon = ICONES[valeur];
              return (
                <label
                  key={valeur}
                  htmlFor={`${p}-service-${valeur}`}
                  className={`cursor-pointer flex flex-col items-center gap-2 rounded-xl border-2 transition-all duration-200 focus-within:ring-2 focus-within:ring-navy/40 ${
                    carte ? 'py-2.5 px-1.5' : 'py-4 px-3'
                  } ${
                    form.service === valeur
                      ? 'border-navy bg-navy text-yellow'
                      : 'border-gray-200 text-navy hover:border-navy/30 hover:bg-navy/[0.02]'
                  }`}
                >
                  <input
                    id={`${p}-service-${valeur}`}
                    type="radio"
                    name="service"
                    value={valeur}
                    checked={form.service === valeur}
                    onChange={() => set('service', valeur)}
                    toolparamdescription={agent(DESCRIPTION_SERVICE)}
                    className="sr-only"
                  />
                  <Icon className={carte ? 'w-5 h-5' : 'w-6 h-6'} />
                  <span className={`font-bold uppercase text-center ${carte ? 'text-[10px] leading-tight' : 'text-xs'}`}>
                    {libelle}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className={carte ? 'space-y-3' : 'space-y-5'}>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${carte ? 'gap-3' : 'gap-4'}`}>
            <div>
              <label htmlFor={`${p}-firstName`} className={labelClass}>Prénom *</label>
              <input
                id={`${p}-firstName`}
                toolparamdescription={agent(DESCRIPTIONS_AGENT.firstName)}
                autoComplete="given-name"
                aria-invalid={submitted && !!errors.firstName}
                aria-describedby={describedBy('firstName', `${p}-firstName-erreur`)}
                type="text"
                name="firstName"
                required
                placeholder={carte ? 'Prénom' : 'Votre prénom'}
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                className={inputClass('firstName')}
              />
              {erreur('firstName', `${p}-firstName-erreur`)}
            </div>
            <div>
              <label htmlFor={`${p}-lastName`} className={labelClass}>Nom *</label>
              <input
                id={`${p}-lastName`}
                toolparamdescription={agent(DESCRIPTIONS_AGENT.lastName)}
                autoComplete="family-name"
                aria-invalid={submitted && !!errors.lastName}
                aria-describedby={describedBy('lastName', `${p}-lastName-erreur`)}
                type="text"
                name="lastName"
                required
                placeholder={carte ? 'Nom' : 'Votre nom'}
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                className={inputClass('lastName')}
              />
              {erreur('lastName', `${p}-lastName-erreur`)}
            </div>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 ${carte ? 'gap-3' : 'gap-4'}`}>
            <div>
              <label htmlFor={`${p}-email`} className={labelClass}>Email *</label>
              <input
                id={`${p}-email`}
                toolparamdescription={agent(DESCRIPTIONS_AGENT.email)}
                autoComplete="email"
                aria-invalid={submitted && !!errors.email}
                aria-describedby={describedBy('email', `${p}-email-erreur`)}
                type="email"
                name="email"
                required
                placeholder={carte ? 'vous@exemple.be' : 'votre@email.com'}
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                className={inputClass('email')}
              />
              {erreur('email', `${p}-email-erreur`)}
            </div>
            <div>
              <label htmlFor={`${p}-phone`} className={labelClass}>Téléphone *</label>
              <input
                id={`${p}-phone`}
                toolparamdescription={agent(DESCRIPTIONS_AGENT.phone)}
                autoComplete="tel"
                aria-invalid={submitted && !!errors.phone}
                aria-describedby={describedBy('phone', `${p}-phone-erreur`)}
                type="tel"
                name="phone"
                required
                placeholder="+32 4XX XX XX XX"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className={inputClass('phone')}
              />
              {erreur('phone', `${p}-phone-erreur`)}
            </div>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 ${carte ? 'gap-3' : 'gap-4'}`}>
            <div>
              <label htmlFor={`${p}-addressFrom`} className={labelClass}>Ville de départ *</label>
              <input
                id={`${p}-addressFrom`}
                toolparamdescription={agent(DESCRIPTIONS_AGENT.cityFrom)}
                autoComplete="address-level2"
                aria-invalid={submitted && !!errors.cityFrom}
                aria-describedby={describedBy('cityFrom', `${p}-addressFrom-erreur`)}
                type="text"
                name="addressFrom"
                required
                placeholder="Ex : Liège"
                value={form.cityFrom}
                onChange={(e) => set('cityFrom', e.target.value)}
                className={inputClass('cityFrom')}
              />
              {erreur('cityFrom', `${p}-addressFrom-erreur`)}
            </div>
            <div>
              <label htmlFor={`${p}-addressTo`} className={labelClass}>Ville d'arrivée *</label>
              <input
                id={`${p}-addressTo`}
                toolparamdescription={agent(DESCRIPTIONS_AGENT.cityTo)}
                aria-invalid={submitted && !!errors.cityTo}
                aria-describedby={describedBy('cityTo', `${p}-addressTo-erreur`)}
                type="text"
                name="addressTo"
                required
                placeholder="Ex : Bruxelles"
                value={form.cityTo}
                onChange={(e) => set('cityTo', e.target.value)}
                className={inputClass('cityTo')}
              />
              {erreur('cityTo', `${p}-addressTo-erreur`)}
            </div>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 ${carte ? 'gap-3' : 'gap-4'}`}>
            <div>
              <label htmlFor={`${p}-date`} className={labelClass}>Date souhaitée</label>
              {/* PAS de `required` : la date est facultative, comme le disent
                  sa description, `validerDevis` et la colonne `move_date`. */}
              <input
                id={`${p}-date`}
                toolparamdescription={agent(DESCRIPTIONS_AGENT.date)}
                aria-invalid={submitted && !!errors.date}
                aria-describedby={describedBy('date', `${p}-date-erreur`)}
                type="date"
                name="date"
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
                className={inputClass('date')}
              />
              {erreur('date', `${p}-date-erreur`)}
            </div>
            <div>
              <label htmlFor={`${p}-volume`} className={labelClass}>Volume estimé</label>
              {/* Le libellé porte le repère de logement (« 2 à 3 pièces ») ;
                  la VALEUR envoyée reste `v.valeur`, celle de la contrainte
                  CHECK de `devis_requests`. */}
              <select
                id={`${p}-volume`}
                toolparamdescription={agent(DESCRIPTION_VOLUME)}
                aria-invalid={submitted && !!errors.volume}
                aria-describedby={describedBy('volume', `${p}-volume-erreur`)}
                name="volume"
                value={form.volume}
                onChange={(e) => set('volume', e.target.value)}
                className={inputClass('volume')}
              >
                <option value="" disabled>Sélectionnez un volume</option>
                {VOLUMES.map((v) => (
                  <option key={v.valeur} value={v.valeur}>{libelleAvecRepere(v)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor={`${p}-message`} className={labelClass}>Message / Précisions</label>
            <textarea
              id={`${p}-message`}
              toolparamdescription={agent(DESCRIPTIONS_AGENT.message)}
              aria-invalid={submitted && !!errors.message}
              aria-describedby={describedBy('message', `${p}-message-erreur`)}
              name="message"
              placeholder={carte
                ? 'Étage, accès, objets spéciaux… (facultatif)'
                : 'Décrivez votre projet : nombre de pièces, étage, accès difficile, objets spéciaux...'}
              rows={carte ? 2 : 5}
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
              className={inputClass('message')}
            />
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer group">
              {/* Obligatoire, juridiquement et fonctionnellement : c'est LE
                  blocage dur du parcours si un agent ne la voit pas. */}
              <input
                type="checkbox"
                name="consent"
                required
                toolparamdescription={agent(DESCRIPTIONS_AGENT.privacy)}
                aria-invalid={submitted && !!errors.privacy}
                aria-describedby={describedBy('privacy', `${p}-consent-erreur`)}
                checked={form.privacy}
                onChange={(e) => set('privacy', e.target.checked)}
                className="mt-1 w-4 h-4 accent-navy rounded"
              />
              <span className={`${carte ? 'text-xs' : 'text-sm'} text-muted group-hover:text-text transition-colors`}>
                J'accepte que mes données soient utilisées pour traiter ma demande conformément à la{' '}
                <Link to="/politique-confidentialite" className="text-navy underline hover:text-navy/70 transition-colors">
                  politique de confidentialité
                </Link>. *
              </span>
            </label>
            {submitted && errors.privacy && (
              <p id={`${p}-consent-erreur`} className="text-red-500 text-xs mt-1 ml-7">{errors.privacy}</p>
            )}
          </div>

          {/* Échec de l'enregistrement. `role="alert"` pour que l'assistance
              vocale l'annonce : sans lui, la personne relance le même envoi
              sans savoir ce qui a échoué. */}
          {erreurEnvoi && (
            <div role="alert" className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3">
              <p className="text-red-700 text-sm font-semibold">{erreurEnvoi}</p>
            </div>
          )}

          <motion.button
            type="submit"
            disabled={loading}
            whileTap={{ scale: 0.98 }}
            className={`w-full bg-yellow text-navy font-bold uppercase rounded-xl flex items-center justify-center gap-2 hover:bg-navy hover:text-yellow transition-colors duration-200 disabled:opacity-70 tracking-wide ${
              carte ? 'py-3.5 text-sm shadow-lg' : 'py-4 text-lg'
            }`}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Envoyer ma demande
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </motion.button>

          <p className="flex items-center justify-center gap-2 text-muted text-xs text-center">
            <Shield className="w-3.5 h-3.5 flex-shrink-0" />
            {carte
              ? 'Gratuit · Sans engagement · Réponse sous 24h'
              : '* Champs obligatoires. Réponse garantie sous 24h ouvrables.'}
          </p>
        </div>
      </form>

      {success && (
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 100 }}
          className="fixed bottom-6 right-6 bg-navy text-yellow rounded-xl px-6 py-4 shadow-xl flex items-center gap-3 z-50"
        >
          <CircleCheck className="w-5 h-5" />
          <span className="font-bold text-sm">Demande envoyée ! Réponse sous 24h.</span>
        </motion.div>
      )}
    </div>
  );
}
